import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { StyledComponentProps } from '../types';
import { useLanguage } from '../i18n';

const HeaderContainer = styled.header<StyledComponentProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled 
    ? 'rgba(10, 10, 10, 0.95)' 
    : 'rgba(10, 10, 10, 0.1)'};
  backdrop-filter: ${props => props.scrolled ? 'blur(20px)' : 'blur(10px)'};
  border-bottom: ${props => props.scrolled 
    ? '1px solid rgba(255, 255, 255, 0.1)' 
    : '1px solid rgba(255, 255, 255, 0.05)'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.scrolled 
    ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
    : 'none'};
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 2.2rem;
  font-weight: 800;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  gap: 8px;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavItem = styled.li`
  .nav-link {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
    font-size: 14px;
    padding: 10px 16px;
    border-radius: 8px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    position: relative;
    cursor: pointer;
    
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      background: ${props => props.theme.gradients.primary};
      transition: all 0.3s ease;
      transform: translateX(-50%);
    }
    
    &:hover {
      color: white;
      background: rgba(255, 255, 255, 0.1);
      transform: translateY(-1px);
      
      &::before {
        width: 80%;
      }
    }
  }
`;


const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 20px;
  color: white;
  cursor: pointer;
  padding: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
  
  @media (max-width: 1024px) {
    display: block;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LangSwitch = styled.div<{ $active: 'en' | 'zh' }>`
  display: flex;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);

  button {
    padding: 6px 10px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.9);
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .en { background: ${props => props.$active === 'en' ? 'rgba(255,255,255,0.18)' : 'transparent'}; }
  .zh { background: ${props => props.$active === 'zh' ? 'rgba(255,255,255,0.18)' : 'transparent'}; }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  
  @media (min-width: 1025px) {
    display: none;
  }
`;

const MobileNavItem = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  .mobile-nav-link {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.3s ease;
    cursor: pointer;
    
    &:hover {
      color: white;
      padding-left: 8px;
    }
  }
`;

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const goHomeAndScroll = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
      setMobileMenuOpen(false);
      return;
    }
    scrollToSection(sectionId);
  };

  const navItems: Array<
    | { type: 'section'; titleKey: string; id: string }
    | { type: 'route'; titleKey: string; to: string }
  > = [
    { type: 'section', titleKey: 'nav.home', id: 'hero' },
    { type: 'section', titleKey: 'nav.products', id: 'products' },
    { type: 'section', titleKey: 'nav.cases', id: 'cases' },
    { type: 'section', titleKey: 'nav.partners', id: 'partners' },
    { type: 'route', titleKey: 'nav.apps', to: '/apps' },
    { type: 'section', titleKey: 'nav.contact', id: 'cta' },
  ];

  return (
    <HeaderContainer scrolled={scrolled}>
      <Nav>
        <Logo onClick={() => navigate('/')}>
          {t('brand.text')}
        </Logo>
        
        <NavMenu>
          {navItems.map((item, index) => (
            <NavItem key={index}>
              <div 
                className="nav-link"
                onClick={() => {
                  if (item.type === 'route') {
                    navigate(item.to);
                    setMobileMenuOpen(false);
                    return;
                  }
                  goHomeAndScroll(item.id);
                }}
              >
                {t(item.titleKey)}
              </div>
            </NavItem>
          ))}
        </NavMenu>

        <RightControls>
          <LangSwitch $active={lang}>
            <button className="en" onClick={() => setLang('en')}>{t('toggle.en')}</button>
            <button className="zh" onClick={() => setLang('zh')}>{t('toggle.zh')}</button>
          </LangSwitch>
          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </MobileMenuButton>
        </RightControls>
      </Nav>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {navItems.map((item, index) => (
              <MobileNavItem key={index}>
                <div 
                  className="mobile-nav-link"
                  onClick={() => {
                    if (item.type === 'route') {
                      navigate(item.to);
                      setMobileMenuOpen(false);
                      return;
                    }
                    goHomeAndScroll(item.id);
                  }}
                >
                  {t(item.titleKey)}
                </div>
              </MobileNavItem>
            ))}
          </MobileMenu>
        )}
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header;